import {
  CreateReportDocument,
  DeleteReportDocument,
  DownloadReportDocumentDocument,
  ReportGetByIdDocument,
  ReportsPaginatedDocument,
  UpdateReportDocument,
  UploadReportDocumentDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { IFile } from "api/services/types";
import { Optional, PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IReportCreateVars,
  IReportDocumentModel,
  IReportModel,
  IReportsPaginatedResponse,
  IReportsPaginatedVars,
  IReportUpdateVars,
  ReportsCreateUpdateResponse,
} from "./types";

export async function getReportsPaginated(
  variables: IReportsPaginatedVars
): Promise<IReportsPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ReportsPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.reportsPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getReport(
  reportId: number
): Promise<Optional<PartialNullable<IReportModel>>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ReportGetByIdDocument,
      variables: { reportId },
      fetchPolicy: "network-only",
    });

    return res?.data?.reportGetById;
  } catch (error) {
    throw error;
  }
}

export async function createReport(
  variables: IReportCreateVars
): Promise<Optional<ReportsCreateUpdateResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: CreateReportDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Report",
      fieldNames: ["reportsPaginated"],
    });

    return res?.data?.reportCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateReport(
  variables: IReportUpdateVars
): Promise<Optional<ReportsCreateUpdateResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UpdateReportDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Report",
      id: variables.reportId.toString(),
      fieldNames: ["reportsPaginated", "reportGetById"],
    });

    return res?.data?.reportUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deleteReport(reportId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: DeleteReportDocument,
      variables: { reportId },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Report",
      id: reportId.toString(),
      fieldNames: ["reportsPaginated"],
    });

    return Boolean(res?.data?.reportDelete);
  } catch (error) {
    throw error;
  }
}

export async function uploadReportDocument(
  variables: IFile
): Promise<Optional<IReportDocumentModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UploadReportDocumentDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (variables.relatedEntityId) {
      executeEviction(client, {
        typename: "Report",
        id: variables.relatedEntityId.toString(),
        fieldNames: ["reportGetById"],
      });
    }

    return res?.data?.reportDocumentUpload;
  } catch (error) {
    throw error;
  }
}

export async function downloadReportDocument(
  reportDocumentId: number
): Promise<Optional<string>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: DownloadReportDocumentDocument,
      variables: { reportDocumentId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.reportDocumentDownload;
  } catch (error) {
    throw error;
  }
}
